import type {
  ReploElement,
  ReploElementMetadata,
} from "schemas/generated/element";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { toast } from "@v1/components/designSystem/alert/Toast";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import {
  archiveElement as archiveElementAction,
  selectDraftElementId,
  selectDraftElementProjectId,
  setEditorMode,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";
import { capitalizeFirstLetter } from "replo-utils/lib/string";

type ArchiveOptions = {
  onSuccess?: () => void;
  from: "modal" | "button";
};

export function useArchiveElement() {
  const dispatch = useEditorDispatch();
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();
  const draftElementId = useEditorSelector(selectDraftElementId);
  const draftElementProjectId = useEditorSelector(selectDraftElementProjectId);
  const setDraftElement = useSetDraftElement();
  const { mutate: archiveMutation } = elementRouter.archive.useMutation();

  const { data: elementsMetadata } =
    elementRouter.listAllElementsMetadata.useQuery(
      projectId ? { projectId } : skipToken,
    );

  const elements = React.useMemo(
    () => elementsMetadata ?? [],
    [elementsMetadata],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Patrick (2025-09-26) code was like this, but now rendering a lint error. we should prob fix it at some point.
  const handleArchive = React.useCallback(
    (
      element: ReploElement | ReploElementMetadata,
      shouldUnpublish: boolean,
      options: ArchiveOptions,
    ) => {
      archiveMutation(
        { elementId: element.id, shouldUnpublish },
        {
          onSuccess: () => {
            toast({
              header: `${capitalizeFirstLetter(
                elementTypeToEditorData[element.type].singularDisplayName,
              )} archived successfully`,
              message: `Your ${capitalizeFirstLetter(
                elementTypeToEditorData[element.type].singularDisplayName,
              )} has been archived successfully`,
              cta: "View archived element",
              ctaOnClick: () => {
                dispatch(setEditorMode("archived"));
              },
            });
            dispatch(archiveElementAction(element.id));
            void elementUtils.listArchived.invalidate();
            void elementUtils.listAllElementsMetadata.invalidate();

            logEvent("element.archive", {
              elementId: element.id,
              elementType: element.type,
              projectId: projectId ?? "",
              from: options.from,
            });

            if (
              draftElementId === element.id &&
              draftElementProjectId &&
              elements.length > 1
            ) {
              const currentIndex = elements.findIndex(
                (p) => p.id === element.id,
              );
              const nextIndex =
                currentIndex === 0 ? currentIndex + 1 : currentIndex - 1;
              const newPageId = elements[nextIndex]!.id;
              setDraftElement({ id: newPageId });
              navigate(
                generatePath(routes.editor.element, {
                  projectId: draftElementProjectId,
                  elementId: newPageId,
                }),
              );
            }

            options.onSuccess?.();
          },
        },
      );
    },
    [
      archiveMutation,
      dispatch,
      draftElementId,
      draftElementProjectId,
      elements,
      navigate,
      setDraftElement,
      logEvent,
    ],
  );

  return handleArchive;
}
